/* eslint-disable @typescript-eslint/no-explicit-any */
import i18n from 'services/i18n';

import { ErrorResult } from 'models/form';
import {
  getAmountRangeErrorMessage,
  testRegex,
  isValidIBAN,
  cardNumberValidation,
  isAlphaNumericOrSpace,
  futureMMYYValidation,
  dateOfBirthValidation
} from 'utilities';
import { formatNumberToStringByLocale } from 'utilities/number';
import {
  MANDATORY_CARD_FIELDS,
  FIELD_NAMES as TOKENIZATION_FIELD_NAMES,
  REGEXES,
  KBA_PASSWORD_LENGTH,
  MINIMUM_PASSED_VALIDATIONS_NUMBER
} from 'consts';

export const SPECIFIC_VALIDATIONS = {
  [TOKENIZATION_FIELD_NAMES.iban]: (
    value: any,
    errors: Record<string, string>,
    regex: string
  ) => isValidIBAN(value, errors, regex),
  [MANDATORY_CARD_FIELDS.number]: (
    value: any,
    errors: Record<string, string>,
    regex: string
  ) => cardNumberValidation(value, errors, regex),
  [MANDATORY_CARD_FIELDS.cardholder]: (value: any, errors: Record<string, string>) => {
    const isValid = isAlphaNumericOrSpace(value);
    if (!isValid) {
      return errors[MANDATORY_CARD_FIELDS.cardholder];
    }
  },
  [MANDATORY_CARD_FIELDS.expiration]: (value: any) => futureMMYYValidation(value),
  [TOKENIZATION_FIELD_NAMES.card_expiration]: (value: any) => futureMMYYValidation(value),
  [TOKENIZATION_FIELD_NAMES.card_number]: (
    value: any,
    errors: Record<string, string>,
    regex: string
  ) => cardNumberValidation(value, errors, regex),
  [TOKENIZATION_FIELD_NAMES.date_of_birth]: (
    value: any,
    errors: Record<string, string>,
    regex: string
  ) => dateOfBirthValidation(value, errors, regex)
};

export const composeValidators =
  (...validators: any[]) =>
  (value: any): ErrorResult =>
    validators.reduce(
      (error, validator) =>
        error || (typeof validator === 'function' ? validator(value) : undefined),
      undefined
    );

export const isRequired = (value: any): ErrorResult =>
  !value ? 'required_field_error' : undefined;

export const isValidField =
  ({
    name,
    errors,
    regex
  }: {
    name: string;
    errors: Record<string, string>;
    regex: string;
  }) =>
  (value: any): ErrorResult => {
    const fieldValidation = SPECIFIC_VALIDATIONS[name];
    if (fieldValidation) {
      return fieldValidation(value, errors, regex);
    }
  };

export const isValidRegex =
  ({ name, regex }: { name: string; regex: string }) =>
  (value: any): ErrorResult => {
    const isValid = testRegex({ regex, value, name });
    return isValid ? undefined : 'invalid_field';
  };

export const lengthGreaterThan =
  (length: number) =>
  (value: any): ErrorResult =>
    value?.length > length ? 'Too long' : undefined;

export const isAmountInRange = (
  value: any,
  currency: string,
  amountRangePerCurrencyMapping: Record<
    string,
    { min_amount: number; max_amount: number }
  >
): ErrorResult => {
  const parsedValue = Number.parseFloat(value);
  const currentCurrencyMapping = (amountRangePerCurrencyMapping || {})[currency];
  const { min_amount: minAmount, max_amount: maxAmount } = currentCurrencyMapping || {};
  const errorMessage = getAmountRangeErrorMessage({
    minAmount,
    maxAmount,
    currency,
    parsedValue
  });

  return errorMessage || undefined;
};

export const isNumberNotZero = (value: any): ErrorResult => {
  return Number(value)
    ? undefined
    : {
        key: 'gt_number',
        params: {
          number: formatNumberToStringByLocale({ number: 0, locale: i18n.language }) || 0
        }
      };
};

export const isValidOtp =
  (codeLength: number) =>
  (value: Array<string>): ErrorResult => {
    const isValid = value?.length === codeLength;
    return isValid ? undefined : 'otp.invalid_otp';
  };

export const isNotValidFirstPin =
  (codeLength: number) =>
  (value: Array<string>): ErrorResult => {
    const isValid = value?.length === codeLength;
    return isValid ? undefined : 'activate_issued_card.invalid_pin';
  };

export const isNotValidPin =
  (codeLength: number) =>
  (firstValue: Array<string>, secondValue: Array<string>): ErrorResult => {
    const isValid =
      !isNotValidFirstPin(codeLength)(firstValue) &&
      secondValue?.length === codeLength &&
      firstValue === secondValue;

    return isValid ? undefined : 'activate_issued_card.second_pin_not_identical';
  };

export const getPassedValidations = (value = '') => {
  const passedValidations: RegExp[] = [];
  const validations = [
    REGEXES.UPPERCASE_LETTER,
    REGEXES.LOWERCASE_LETTER,
    REGEXES.DIGIT,
    REGEXES.SYMBOL
  ];

  validations.forEach(validation => {
    if (validation.test(value)) {
      passedValidations.push(validation);
    }
  });

  return passedValidations;
};

export const isValidKbaPasswordLength = (
  value = '',
  minLength: number,
  maxLength: number
) => {
  return value.length >= minLength && value.length <= maxLength;
};

export const isNotValidFirstKbaPassword = (value = ''): ErrorResult => {
  const { MIN, MAX } = KBA_PASSWORD_LENGTH;
  if (!isValidKbaPasswordLength(value, MIN, MAX)) {
    return {
      key: 'activate_issued_card.invalid_password_length',
      params: { min: MIN, max: MAX }
    };
  }
  const passedValidations = getPassedValidations(value);

  return passedValidations.length >= MINIMUM_PASSED_VALIDATIONS_NUMBER
    ? undefined
    : 'activate_issued_card.invalid_password';
};

export const isNotValidConfirmKbaPassword = (
  firstValue = '',
  secondValue = ''
): ErrorResult => {
  const isValid = !isNotValidFirstKbaPassword(firstValue) && firstValue === secondValue;

  return isValid ? undefined : 'activate_issued_card.second_password_not_identical';
};
